import {
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EIconButtonVariant,
  ELinkColorVariant,
  ELinkVariant,
  ETextStyleVariant,
  GeneralCaretLeftIcon,
  Heading,
  IconButton,
  Link,
  Modal,
  NumberInput,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';

type TMobileSearchGuestsModalContentProps = {
  onCloseHandler: () => void;
  guests?: Partial<IHeaderSearchFilterGuests>;
  onChangeGuests?: (guests: IHeaderSearchFilterGuests) => void;
  isGuestOccupancyAvailable?: boolean;
};

export const MobileSearchGuestsModalContent = ({
  onCloseHandler,
  guests,
  onChangeGuests,
  isGuestOccupancyAvailable,
}: TMobileSearchGuestsModalContentProps) => {
  const intl = useIntl();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const [adultsValue, setAdultsValue] = useState(guests?.adults || 1);
  const [childrenValue, setChildrenValue] = useState(guests?.children || 0);
  const [infantsValue, setInfantsValue] = useState(guests?.infants || 0);

  // Track initial guest values to detect changes
  const initialGuestsRef = useRef({
    adults: guests?.adults || 1,
    children: guests?.children || 0,
    infants: guests?.infants || 0,
    accessible: guests?.accessible || false,
    pet_friendly: guests?.pet_friendly || false,
  });

  // Check if guests have changed from initial values
  const hasChanges = useMemo(() => {
    const initial = initialGuestsRef.current;
    return (
      adultsValue !== initial.adults ||
      childrenValue !== initial.children ||
      infantsValue !== initial.infants
    );
  }, [adultsValue, childrenValue, infantsValue]);

  // Handle back button - discard changes and close (same as other sheets)
  const handleBackButton = () => {
    // Don't save changes, just close the modal
    onCloseHandler();
  };

  const onClearHandler = () => {
    setAdultsValue(1); // Reset to default minimum of 1 adult
    setChildrenValue(0);
    setInfantsValue(0);
  };

  const onDoneHandler = () => {
    const newGuests = {
      adults: adultsValue || undefined,
      children: childrenValue || undefined,
      ...(isGuestOccupancyAvailable && { infants: infantsValue || undefined }),
      seatbelts: guests?.seatbelts || undefined,
    };
    onChangeGuests?.(newGuests);
    onCloseHandler();
  };

  const getModalTitle = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      // Default behavior
      return <FormattedMessage defaultMessage="Guest" id="zF7Cth" />;
    }

    // Both rental and delivery modes show the same text according to requirements
    return <FormattedMessage defaultMessage="Add guests" id="pP5ifN" />;
  };

  return (
    <>
      <Modal.Header inlineHeader={true} className="flex items-center !p-0 gap-1">
        <IconButton
          icon={GeneralCaretLeftIcon}
          onClick={handleBackButton}
          className="text-[21px]"
          variant={EIconButtonVariant.Ghost}
        />
        <Heading variant={EHeadingStyleVariant.H6}>{getModalTitle()}</Heading>
      </Modal.Header>
      <Modal.Content className="h-full !mb-0 !my-6 !p-0">
        <div className="flex flex-col p-0 pointer-events-auto md:p-4 gap-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="Adults" id="rePX4c" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                <FormattedMessage defaultMessage="Ages 13 or above" id="7GtkzA" />
              </Text>
            </div>
            <NumberInput
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Adults number input',
                id: 'QI9jVh',
              })}
              value={adultsValue}
              onChange={setAdultsValue}
              minValue={1}
              maxValue={10}
            />
          </div>
          <Divider />
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="Children" id="kzlY5R" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                {isGuestOccupancyAvailable ? (
                  <FormattedMessage defaultMessage="Ages 3-12" id="cn4PHX" />
                ) : (
                  <FormattedMessage defaultMessage="Ages 12 or under" id="SAdssW" />
                )}
              </Text>
            </div>
            <NumberInput
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Children number input',
                id: 'ElS1Or',
              })}
              value={childrenValue}
              onChange={setChildrenValue}
              maxValue={10}
            />
          </div>
          {isGuestOccupancyAvailable && (
            <>
              <Divider />
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <Text variant={ETextStyleVariant.MediumBold}>
                    <FormattedMessage defaultMessage="Infants" id="wHlQeN" />
                  </Text>
                  <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                    <FormattedMessage defaultMessage="Ages 2 or under" id="/cJQwH" />
                  </Text>
                </div>
                <NumberInput
                  ariaLabel={intl.formatMessage({
                    defaultMessage: 'Infants number input',
                    id: 'EBh6e/',
                  })}
                  value={infantsValue}
                  onChange={setInfantsValue}
                  maxValue={10}
                />
              </div>
              <Divider />
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer className="!p-0 !pt-8">
        <div className="flex items-center justify-between w-full">
          <Link
            label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            variant={ELinkVariant.Underline}
            colorVariant={ELinkColorVariant.Primary}
            onClick={onClearHandler}
          />
          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
            onClick={onDoneHandler}
            disabled={!hasChanges}
          />
        </div>
      </Modal.Footer>
    </>
  );
};
